import { isEmpty } from 'lodash';
import { useRecoilValue } from 'recoil';

import { Badge } from '@/components/badge';
import { CarrierLogo } from '@/components/carrier-logo';
import { Heading } from '@/components/heading';
import { RatesSpotBreakdown } from '@/components/modules/rates/spot-breakdown';
import { Text } from '@/components/text';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { MODE_WATER } from '@/lib/constants';
import { getCarrierName } from '@/lib/models/carrier/utils/get-carrier-name';
import { SpotRateModel } from '@/lib/models/rates/types';
import { getCarriersByIdsState } from '@/state/carriers';
import { getPreferredIdentifier } from '@/utils/helpers';
import { tv } from '@/utils/styles';

export interface JourneyRatesCardProps {
  rates?: SpotRateModel[];
}

const JourneyRatesCard = ({ rates }: JourneyRatesCardProps) => {
  const carrierIds = (rates || []).map((rate) => rate.carrierId);
  const carriers = useRecoilValue(getCarriersByIdsState(carrierIds));
  const filteredCarriers = carriers.filter((carrier) => carrier.id);
  const hasRates = !isEmpty(rates);

  const { base, metadata } = styles();

  return (
    <section className={base()}>
      <Heading variant="h4" as="h2" className="m-0">
        Shipping rates for this service
      </Heading>
      {hasRates && (
        <Accordion type="single" defaultValue={carrierIds[0]}>
          {filteredCarriers.map((carrier, index) => {
            const carrierKey = carrier.id || `carrier-${index}`;
            const carrierCode = getPreferredIdentifier(carrier.identifiers)?.value;
            const carrierName = getCarrierName(carrier);
            const thisRate = rates && rates[index];

            return (
              <AccordionItem key={carrierKey} value={carrierKey}>
                <AccordionTrigger className="py-2">
                  <span className="flex items-center gap-2">
                    {carrierCode && <CarrierLogo carrierCode={carrierCode} size="6" mode={MODE_WATER} />}
                    {carrierName}
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className={metadata()}>
                    <Badge variant="dark">Spot Rate</Badge>
                    <Badge>1x 20ft Container</Badge>
                  </div>
                  {thisRate ? <RatesSpotBreakdown rate={thisRate} /> : <p>No rates available</p>}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
      {!hasRates && (
        <div>
          <Text variant="secondary" className="m-0">
            No rates currently available for this service.
          </Text>
        </div>
      )}
    </section>
  );
};

const styles = tv({
  slots: {
    base: 'flex flex-col gap-4 rounded-lg border border-grey-200 bg-white p-4 md:p-8',
    metadata: 'mb-4 mt-2 flex gap-2',
  },
});

export { JourneyRatesCard };
